export interface DimensionSignal {
  dimension: string;
  summary: string;
  skipped: boolean;
}

export interface AdrConflict {
  adr: string;
  description: string;
}

export interface DimensionParseResult {
  dimensionComplete?: DimensionSignal;
  adrConflict?: AdrConflict;
  promptGenerationUnlocked?: boolean;
  /** Content with all XML signal tags stripped. */
  strippedContent: string;
}

/**
 * Parses agent response content for dimension-tracking XML signals.
 * Returns extracted signal data and the content with tags removed.
 */
export function parseDimensionSignals(content: string): DimensionParseResult {
  let strippedContent = content;
  let dimensionComplete: DimensionSignal | undefined;
  let adrConflict: AdrConflict | undefined;
  let promptGenerationUnlocked: boolean | undefined;

  // Parse <dimension_complete>...</dimension_complete>
  const dimMatch = content.match(/<dimension_complete>([\s\S]*?)<\/dimension_complete>/);
  if (dimMatch) {
    const inner       = dimMatch[1];
    const dimName     = inner.match(/<dimension>([\s\S]*?)<\/dimension>/)?.[1]?.trim();
    const summary     = inner.match(/<summary>([\s\S]*?)<\/summary>/)?.[1]?.trim() ?? '';
    const skippedText = inner.match(/<skipped>([\s\S]*?)<\/skipped>/)?.[1]?.trim();
    if (dimName) {
      dimensionComplete = { dimension: dimName, summary, skipped: skippedText === 'true' };
    }
    strippedContent = strippedContent
      .replace(/<dimension_complete>[\s\S]*?<\/dimension_complete>/g, '')
      .trim();
  }

  // Parse <adr_conflict>...</adr_conflict>
  const adrMatch = content.match(/<adr_conflict>([\s\S]*?)<\/adr_conflict>/);
  if (adrMatch) {
    const inner   = adrMatch[1];
    const adrName = inner.match(/<adr>([\s\S]*?)<\/adr>/)?.[1]?.trim();
    const desc    = inner.match(/<description>([\s\S]*?)<\/description>/)?.[1]?.trim();
    if (adrName && desc) {
      adrConflict = { adr: adrName, description: desc };
    }
    strippedContent = strippedContent
      .replace(/<adr_conflict>[\s\S]*?<\/adr_conflict>/g, '')
      .trim();
  }

  // Parse <prompt_generation_unlocked>...</prompt_generation_unlocked>
  const unlockedMatch = content.match(
    /<prompt_generation_unlocked>([\s\S]*?)<\/prompt_generation_unlocked>/,
  );
  if (unlockedMatch) {
    promptGenerationUnlocked = unlockedMatch[1].trim() === 'true';
    strippedContent = strippedContent
      .replace(/<prompt_generation_unlocked>[\s\S]*?<\/prompt_generation_unlocked>/g, '')
      .trim();
  }

  return { dimensionComplete, adrConflict, promptGenerationUnlocked, strippedContent };
}
